import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GitCommitWithCaseCorrectedPaths } from '@common/git/models/git-commit-with-case-corrected-paths.model';
import { GitDiff } from '@common/git/models/git-diff.model';
import { MadCloudCommitResponse } from '@common/git/models/mad-cloud-commit-response.model';
import { MadCloudCommit } from '@common/git/models/mad-cloud-commit.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { CommitAction } from '@portal-core/commits/models/commit-action.model';
import { SharePendingRequest } from '@portal-core/util/observables.decorators';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CommitsApiService {
  constructor(private http: HttpClient, private apiService: ApiService) { }

  @SharePendingRequest()
  getCommitById$(projectId: number, commitId: string, includeDiff: boolean = false): Observable<MadCloudCommit> {
    return this.http.get<MadCloudCommitResponse>(`${this.apiService.centralApiBaseUri}/api/git/projects/${projectId}/commit/${commitId}${includeDiff ? '?includeDiff=true' : ''}`).pipe(
      map(commitData => {
        const commit = commitData.Commit;
        if (commit) {
          commit.Diffs = commitData.FileDiffs;
        }
        return commit;
      })
    );
  }

  @SharePendingRequest()
  getProjectCommits$(projectId: number, branchName: string, includeAllUsers: boolean = true, filter: PageFilter): Observable<Page<MadCloudCommit>> {
    return this.http.post<Page<MadCloudCommit>>(
      `${this.apiService.centralApiBaseUri}/api/git/projects/${projectId}/commitsGrid?branchName=${encodeURIComponent(branchName)}&allUsers=${includeAllUsers}`,
      filter
    );
  }

  @SharePendingRequest()
  getFileCommits$(projectId: number, branchName: string, path: string, filter: PageFilter = null): Observable<Page<MadCloudCommit>> {
    return this.http.post<Page<MadCloudCommit>>(
      `${this.apiService.centralApiBaseUri}/api/projects/${projectId}/File/CommitsGrid?branchName=${branchName}&path=${encodeURIComponent(path).replace(/\(/g, '%28').replace(/\)/g, '%29')}`,
      filter
    );
  }

  @SharePendingRequest()
  getFileCommitDiff$(projectId: number, commitId: string, filePath: string): Observable<GitDiff> {
    const fileDiffRequest = {
      Path: filePath
    };
    return this.http.post<GitDiff>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/File/Commits/${commitId}/Diff`, fileDiffRequest);
  }

  @SharePendingRequest()
  getDuplicateFilePaths$(projectId: number, branchName: string, filePaths: string[]): Observable<string[]> {
    return this.http.post<string[]>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/File/DuplicateFilePaths?branch=${branchName}`, filePaths)
  }

  postNewCommit$(projectId: number, branchName: string, commitActions: CommitAction[], commit: string, email: string, fullName: string): Observable<GitCommitWithCaseCorrectedPaths> {
    return this.http.post<GitCommitWithCaseCorrectedPaths>(`${this.apiService.centralApiBaseUri}/api/projects/${projectId}/File/PostCommit`, {
      branch: decodeURIComponent(branchName),
      commit_message: commit,
      author_email: email,
      author_name: fullName,
      actions: commitActions
    })
  }
}
